// dotStates.js
// ────────────────────────────────────────────────────────────────────────────
// Three-stage workflow indicator (Picked → Pressed → Packed) for V2.
//
// V1 rules — each stage read INDEPENDENTLY from portalState. No cascade
// inference at the row level. The backend already does the lifts (e.g. when
// an FO ships, the cascade writes pack_instances with status='passed' so
// packed renders correctly without UI cascade math).
//
// Per-instance maps are authoritative when present:
//   - pack_instances[i].status === 'passed' → packed
//   - press_instances[i].pressed === true   → pressed
//   - qty_picked compared to total          → picked
//
// Non-applicable cells (standalone Pressed; decoration Pressed+Packed) show
// the slash-circle ('na').
// ────────────────────────────────────────────────────────────────────────────

import { EMBELLISHMENT_TYPES } from '../pressingHelpers';

export function psForSku(portalState, sku) {
    if (!portalState || !sku) return null;
    const matches = [];
    if (portalState[sku]) matches.push(portalState[sku]);
    for (const [k, v] of Object.entries(portalState)) {
        if (!v || k === sku) continue;
        if (k.startsWith(sku + '!') || k.startsWith(sku + '#') || k.startsWith(sku + '|')) {
            matches.push(v);
        }
    }
    if (matches.length === 0) return null;
    const agg = { qty_total: 0, qty_picked: 0, qty_pressed: 0, qty_packed: 0 };
    for (const m of matches) {
        const passedPacks = m.pack_instances
            ? Object.values(m.pack_instances).filter(x => x?.status === 'passed').length
            : 0;
        const pressedPresses = m.press_instances
            ? Object.values(m.press_instances).filter(x => x?.pressed).length
            : 0;
        agg.qty_total   += m.qty_total   || 0;
        agg.qty_picked  += m.qty_picked  || 0;
        agg.qty_pressed += Math.max(m.qty_pressed || 0, pressedPresses);
        agg.qty_packed  += Math.max(m.qty_packed  || 0, passedPacks);
    }
    return agg;
}

export function anyPickingDone(portalState) {
    return Object.values(portalState || {}).some(s => (s.qty_picked || 0) > 0);
}

function stateFromCount(done, total) {
    if (total <= 0) return 'todo';
    if (done >= total) return 'done';
    if (done > 0) return 'partial';
    return 'todo';
}

// Compute the three-stage state for a single line-item-ish thing.
//   kind   = 'decoration' | 'standalone' | 'bundle'
//   target = { sku, quantity }
//   ps     = aggregated portalState entry (psForSku result) or null
// `flag` = the backend's per-line verdict (fo._lineItemFlags[lineItemId] =
// {picked, packed, pressed}). V1 rule (2026-05-23): when the flag exists it
// wins over the portalState-derived count — the backend already applied real
// events + the shipped/transferred cascade + shipped-claim subtraction per FO.
// A picked flag is only downgraded to partial when portalState shows partial
// coverage for this instance (mirrors V1's LineItemRow / BundleRows refine).
export function applyLineFlag(flag, counts, total) {
    if (!flag) return counts;
    const out = { ...counts };
    if (flag.picked === true) {
        const refine = counts.picked > 0 && counts.picked < total;
        out.picked = refine ? counts.picked : total;
    } else if (flag.picked === false) {
        out.picked = 0;
    }
    if (flag.pressed === true)       out.pressed = total;
    else if (flag.pressed === false) out.pressed = 0;
    if (flag.packed === true)        out.packed = total;
    else if (flag.packed === false)  out.packed = 0;
    return out;
}

export function dotsForRow(kind, target, ps, _shipped = false, flag = null) {
    const total   = target?.quantity || 1;
    const counts  = applyLineFlag(flag, {
        picked:  ps?.qty_picked  || 0,
        pressed: ps?.qty_pressed || 0,
        packed:  ps?.qty_packed  || 0,
    }, total);
    const picked  = counts.picked;
    const pressed = counts.pressed;
    const packed  = counts.packed;

    if (kind === 'decoration') {
        return {
            picked:  { state: stateFromCount(picked, total), label: `${picked}/${total}` },
            pressed: { state: 'na' },
            packed:  { state: 'na' },
        };
    }
    if (kind === 'standalone') {
        return {
            picked:  { state: stateFromCount(picked, total), label: `${picked}/${total}` },
            pressed: { state: 'na' },
            packed:  { state: stateFromCount(packed, total), label: `${packed}/${total}` },
        };
    }
    return {
        picked:  { state: stateFromCount(picked,  total), label: `${picked}/${total}` },
        pressed: { state: stateFromCount(pressed, total), label: `${pressed}/${total}` },
        packed:  { state: stateFromCount(packed,  total), label: `${packed}/${total}` },
    };
}

// Bundle rollup: Picked rolls up parent + every decoration child (V1 rule).
// Pressed + Packed track the parent only.
// `lineFlags` = merged fo._lineItemFlags across the order's FOs (line item id →
// {picked, packed, pressed}); when present it wins per line (V1 rule).
// Pressed is only a real stage for a bundle with decoration children; a
// standalone garment / hat has nothing to press → 'na' (mirrors V1, which
// never renders a Pressed badge on a non-bundle row).
export function dotsForBundle(parent, children, portalState, _shipped = false, lineFlags = null) {
    const parentPs = psForSku(portalState, parent.sku) || (portalState[parent.sku] || null);
    const parentQty = parent.quantity || 1;
    const pCounts = applyLineFlag(lineFlags?.[parent.id], {
        picked:  parentPs?.qty_picked  || 0,
        pressed: parentPs?.qty_pressed || 0,
        packed:  parentPs?.qty_packed  || 0,
    }, parentQty);

    let picked = Math.min(pCounts.picked, parentQty);
    let total  = parentQty;
    let allFullyPicked = picked >= parentQty;
    let anyPicked      = picked > 0;
    let pressable = false;
    for (const c of children) {
        if (isDecoration(c)) pressable = true;
        const cps  = psForSku(portalState, c.sku) || (portalState[c.sku] || null);
        const cqty = c.quantity || 1;
        const cCounts = applyLineFlag(lineFlags?.[c.id], { picked: cps?.qty_picked || 0 }, cqty);
        const cpicked = Math.min(cCounts.picked, cqty);
        picked += cpicked;
        total  += cqty;
        if (cpicked < cqty) allFullyPicked = false;
        if (cpicked > 0) anyPicked = true;
    }
    const pickedState = allFullyPicked ? 'done' : anyPicked ? 'partial' : 'todo';

    const pressed = pCounts.pressed;
    const packed  = pCounts.packed;

    return {
        picked:  { state: pickedState, label: `${picked}/${total}` },
        pressed: pressable
            ? { state: stateFromCount(pressed, parentQty), label: `${pressed}/${parentQty}` }
            : { state: 'na' },
        packed:  { state: stateFromCount(packed,  parentQty), label: `${packed}/${parentQty}` },
    };
}

export function isDecoration(li) {
    return EMBELLISHMENT_TYPES.has(li.productType);
}

export function bundleKind(parent, children) {
    const pressableChildren = (children || []).filter(c => isDecoration(c));
    return pressableChildren.length > 0 ? 'bundle' : 'standalone';
}

export function rollupStates(stateList) {
    const out = { picked: 'todo', pressed: 'todo', packed: 'todo' };
    for (const stage of ['picked', 'pressed', 'packed']) {
        let allDone = true, allNa = true, anyDone = false, anyPartial = false;
        for (const s of stateList) {
            const v = s[stage]?.state;
            if (v !== 'na') allNa = false;
            if (v === 'done')    anyDone = true;
            else if (v === 'partial') anyPartial = true;
            else if (v === 'todo')    allDone = false;
            else if (v === 'na')      { /* ignore */ }
            else                      allDone = false;
        }
        if (allNa) out[stage] = 'na';
        else if (allDone && anyDone) out[stage] = 'done';
        else if (anyDone || anyPartial) out[stage] = 'partial';
        else out[stage] = 'todo';
    }
    return {
        picked:  { state: out.picked  },
        pressed: { state: out.pressed },
        packed:  { state: out.packed  },
    };
}

export const STAGES = ['picked', 'pressed', 'packed'];
export const STAGE_LABELS = {
    picked:  'Picked',
    pressed: 'Pressed',
    packed:  'Packed',
};
